import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Save,
  Clear,
  Refresh,
  ThumbUp,
  ThumbDown,
  PriorityHigh,
  ExpandMore,
  Dashboard as DashboardIcon,
  NavigateNext,
  VerifiedUser,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import axios from '../api/client';
import { useBR } from '../contexts/BRContext';
import AuditPdfViewer from './AuditPdfViewer';
import {
  buildHeaderFieldCoordinateMap,
  buildLineItemFieldRects,
  lineItemUnionRect
} from '../utils/auditCoordinates';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

// ─── Background prefetch cache ────────────────────────────────────────────────
// Module-level so it survives React re-mounts when navigating between POs.
const _poCache = {};      // { [poId]: { poData, editedData, editedLineItems, status, pdfFile, pdfError } }
const _prefetching = new Set(); // track in-flight requests to avoid duplicates

async function prefetchPO(poId) {
  const key = String(poId);
  if (_poCache[key] || _prefetching.has(key)) return;
  _prefetching.add(key);
  try {
    const poRes = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}`);
    const data = poRes.data;
    let pdfFile = null;
    let pdfError = '';
    try {
      pdfFile = { data: await fetchPurchaseOrderPdfBytes(poId) };
    } catch (e) {
      pdfError = e?.message || 'Could not load the PDF for this purchase order.';
      pdfFile = purchaseOrderPdfUrl(poId); // iframe / URL fallback
    }
    _poCache[key] = {
      poData: data,
      editedData: stripPoMeta(data),
      editedLineItems: Array.isArray(data.line_items) ? data.line_items.map(i => ({ ...i })) : [],
      status: data.status || 'draft',
      pdfFile,
      pdfError,
    };
  } catch {
    // Prefetch errors are silent — the normal fetch path handles errors
  } finally {
    _prefetching.delete(key);
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const _AE_FIELDS = [
    // header
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'customer', 'factory', 'shipmode', 'incoterm', 'incoterm_origin',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number', 'department',
    'row_number', 'pack_method_type', 'color', 'secondary_color', 'size', 'upc',
    'pieces_to_ship', 'unit_of_measure', 'pack_quantity', 'qty_of_prepacks',
    'us_retail_price', 'sell_price', 'total_row_dollar_value', 'prepack_sku',
    'prepack_article_number', 'class_code', 'hts_description',
    'ae_store_type_code', 'customer_style_description',
];
const VENDOR_MAPPED_FIELDS = {
  'american-eagle':      _AE_FIELDS,
  'american_eagle':      _AE_FIELDS,
  'american_eagle_mens': _AE_FIELDS,
  'american-eagle-mens': _AE_FIELDS,
  'aerie':               _AE_FIELDS,
  'chico': [
    // header
    'customer_po_number', 'initial_order_date', 'bshipdate', 'dc_date', 'customer',
    'ship_to', 'vendor_name_number', 'factory', 'shipmode', 'incoterm', 'po_status',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description',
  ],
  'freepeople': [
    // header
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'free_people_vendor_style_number',
  ],
  'lane-bryant': [
    // header
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm', 'incoterm_origin',
    'lane_pickup_date_or_deliver_before',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
    'lanebryant_size', 'packaging', 'lanebryant_vendor_style_number',
  ],
  'mast': [
    // header
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm', 'incoterm_origin',
    'po_status', 'season',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ],
  'loft': [
    // header
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'customer',
    'ship_to', 'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    'incoterm_origin', 'po_status', 'season', 'lane_pickup_date_or_deliver_before',
    'loft_ticket_type', 'ticket_description',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ],
  'talbots': [
    // header
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'customer',
    'ship_to', 'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    'incoterm_origin', 'po_status', 'season', 'lane_pickup_date_or_deliver_before',
    // line items
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ],
};
// Sub-vendor aliases share the same field list as their parent (snake_case legacy keys)
VENDOR_MAPPED_FIELDS['pink']               = VENDOR_MAPPED_FIELDS['mast'];
VENDOR_MAPPED_FIELDS['loft_outlet']        = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['ann_taylor']         = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['ann_taylor_factory'] = VENDOR_MAPPED_FIELDS['loft'];
// customerCode aliases (post-migration)
VENDOR_MAPPED_FIELDS['BAE']  = VENDOR_MAPPED_FIELDS['american-eagle'];
VENDOR_MAPPED_FIELDS['BAM']  = VENDOR_MAPPED_FIELDS['american-eagle'];
VENDOR_MAPPED_FIELDS['BAR']  = VENDOR_MAPPED_FIELDS['american-eagle'];
VENDOR_MAPPED_FIELDS['BC']   = VENDOR_MAPPED_FIELDS['chico'];
VENDOR_MAPPED_FIELDS['BFP']  = VENDOR_MAPPED_FIELDS['freepeople'];
VENDOR_MAPPED_FIELDS['BLB']  = VENDOR_MAPPED_FIELDS['lane-bryant'];
VENDOR_MAPPED_FIELDS['BVS']  = VENDOR_MAPPED_FIELDS['mast'];
VENDOR_MAPPED_FIELDS['BVP']  = VENDOR_MAPPED_FIELDS['mast'];
VENDOR_MAPPED_FIELDS['BLF']  = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['BLFO'] = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['BAT']  = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['BATF'] = VENDOR_MAPPED_FIELDS['loft'];
VENDOR_MAPPED_FIELDS['BTL']  = VENDOR_MAPPED_FIELDS['talbots'];

const VENDOR_DB_TO_SELECT = {
  // snake_case (legacy pre-migration)
  american_eagle:      'american-eagle',
  american_eagle_mens: 'american-eagle',
  aerie:               'american-eagle',
  chico:               'chico',
  freepeople:          'freepeople',
  lane_bryant:         'lane-bryant',
  mast:                'mast',
  pink:                'mast',
  loft:                'loft',
  loft_outlet:         'loft',
  ann_taylor:          'loft',
  ann_taylor_factory:  'loft',
  talbots:             'talbots',
  // customerCode (post-migration)
  BAE:  'american-eagle',
  BAM:  'american-eagle',
  BAR:  'american-eagle',
  BC:   'chico',
  BFP:  'freepeople',
  BLB:  'lane-bryant',
  BVS:  'mast',
  BVP:  'mast',
  BLF:  'loft',
  BLFO: 'loft',
  BAT:  'loft',
  BATF: 'loft',
  BTL:  'talbots',
};

function isReviewableStatus(s) {
  return s === 'draft' || s === 'pending';
}

/** Direct API URL for the PO PDF — same URL that opens correctly in the browser. */
function purchaseOrderPdfUrl(poId) {
  return `${API_BASE_URL}/api/purchase-orders/${poId}/document`;
}

/** Fetch PDF bytes via axios (same CORS path as the rest of the app). */
async function fetchPurchaseOrderPdfBytes(poId) {
  const res = await axios.get(purchaseOrderPdfUrl(poId), {
    responseType: 'arraybuffer',
    headers: { Accept: 'application/pdf,*/*' },
  });
  const bytes = new Uint8Array(res.data || []);
  if (!bytes.byteLength) {
    throw new Error('PDF response was empty');
  }
  const head = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4]);
  if (!head.startsWith('%PDF')) {
    throw new Error('Response was not a PDF');
  }
  return bytes;
}

const stripPoMeta = (data) => {
  const copy = { ...data };
  delete copy.id;
  delete copy.document_id;
  delete copy.status;
  delete copy.created_at;
  delete copy.updated_at;
  delete copy.header_coordinates;
  delete copy.grid_coordinates;
  delete copy.line_items;
  delete copy.headerFieldRects;
  return copy;
};

const AuditPage = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Batch processed PO IDs — deduplicated so the same PO never appears twice
  const processedPoIds  = [...new Set((location.state?.processedPoIds || []).map(String))].map(Number);
  const poNumberMap     = location.state?.poNumberMap     || {};   // { dbId → customer_po_number }
  const currentBatchIdx = processedPoIds.findIndex(id => String(id) === String(poId));
  const isBatchMode     = processedPoIds.length > 1;

  // IDs already reviewed before arriving on this page (carried in nav state).
  const reviewedPoIds = new Set((location.state?.reviewedPoIds || []).map(String));

  // After marking the current PO as reviewed, which is the first still-unreviewed PO?
  const nextUnreviewedId = processedPoIds.find(id => {
    const afterCurrent = new Set([...reviewedPoIds, String(poId)]);
    return !afterCurrent.has(String(id));
  });

  // ── All useState declarations must come before any derived values that read
  //    from state, otherwise `const` TDZ throws "Cannot access '<var>' before
  //    initialization" at runtime (minified as a single-letter crash).
  const [loading, setLoading] = useState(true);
  // Separate flag for mid-session PO switches: keeps existing content visible
  // instead of replacing the whole page with a spinner.
  const [transitioning, setTransitioning] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [poData, setPoData] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState('');
  const [editedData, setEditedData] = useState({});
  const [editedLineItems, setEditedLineItems] = useState([]);
  const [status, setStatus] = useState('draft');
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [hoverHeaderKey, setHoverHeaderKey] = useState(null);
  const [hoverLineRow, setHoverLineRow] = useState(null);
  const [hoverLineCol, setHoverLineCol] = useState(null);

  // ── Inline BR verification (auto-triggered on each PO load) ─────────────────
  // Replaces the manual "Verify BR" header button for the audit screen.
  const [brInlineData,         setBrInlineData]         = useState(null);
  const [brInlineLoading,      setBrInlineLoading]      = useState(false);
  // { [fieldKey]: 'accept' | 'dismiss' } — per-field decision made by the user
  const [brFieldDecisions,     setBrFieldDecisions]     = useState({});
  // true once the user has handled line-item variances (applied or dismissed)
  const [brLineItemsDone,      setBrLineItemsDone]      = useState(false);
  // true once the user dismisses the "no BR data / PO not found" notification
  const [brNoBrDataDismissed,  setBrNoBrDataDismissed]  = useState(false);
  // controls the line-item BR-changes detail dialog
  const [brLiDialogOpen,       setBrLiDialogOpen]       = useState(false);
  // tracks the latest BR fetch so stale responses from previous POs are discarded
  const brFetchIdRef = useRef(0);

  // Re-run BR verification automatically when a sync completes while on this screen.
  // Deps only reference state/params declared above this line to avoid TDZ crashes.
  const { syncDone } = useBR();
  const prevSyncDoneRef = useRef(false);
  useEffect(() => {
    const vendor = (poData?.vendor || '').toLowerCase();
    const isAE = ['american-eagle', 'american_eagle', 'ae', 'american_eagle_mens', 'american-eagle-mens', 'aerie', 'bae', 'bam', 'bar'].includes(vendor);
    if (syncDone && !prevSyncDoneRef.current && isAE && poId) {
      prevSyncDoneRef.current = true;
      setBrNoBrDataDismissed(false);
      setBrInlineData(null);
      setBrInlineLoading(true);
      const fetchId = Date.now();
      brFetchIdRef.current = fetchId;
      axios.get(`${API_BASE_URL}/api/bamboo-rose/verify/${poId}`)
        .then(res  => { if (brFetchIdRef.current === fetchId) setBrInlineData(res.data); })
        .catch(err => { if (brFetchIdRef.current === fetchId) setBrInlineData({ error: err.response?.data?.detail || err.message }); })
        .finally(() => { if (brFetchIdRef.current === fetchId) setBrInlineLoading(false); });
    }
    if (!syncDone) prevSyncDoneRef.current = false;
  }, [syncDone, poId, poData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Baseline snapshot of the last-saved (or last-loaded) state — used to detect unsaved changes.
  // Stored as JSON strings for cheap deep-equality checks. Must be state (not a ref) so that
  // setting it on load triggers a re-render and useMemo re-evaluates correctly.
  const [savedBaseline, setSavedBaseline] = useState(null); // { data: string, lineItems: string, status: string }

  // True when editedData / editedLineItems / status differ from the last saved baseline.
  const hasUnsavedChanges = useMemo(() => {
    if (!savedBaseline) return false;
    return (
      JSON.stringify(editedData)      !== savedBaseline.data      ||
      JSON.stringify(editedLineItems) !== savedBaseline.lineItems ||
      status                          !== savedBaseline.status
    );
  }, [editedData, editedLineItems, status, savedBaseline]);

  // True only when every PO in the batch has been reviewed AND the current one is too.
  // Must be after `status` useState above — reads from it.
  const allBatchReviewed =
    isBatchMode &&
    (status === 'accepted' || status === 'rejected') &&
    !nextUnreviewedId;

  // Helper: build location state for any navigate() call in batch mode.
  // includeCurrentAsReviewed = true  → used after accept/reject
  // includeCurrentAsReviewed = false → used for manual "Next PO" navigation
  const batchNavState = (includeCurrentAsReviewed = false) => ({
    processedPoIds,
    poNumberMap,
    reviewedPoIds: [
      ...reviewedPoIds,
      ...(includeCurrentAsReviewed ? [String(poId)] : []),
    ],
  });

  // "Effective" reviewed set: carry the nav-state set PLUS the current PO if it
  // has already been accepted/rejected on this page (live status).  Used to
  // compute the remaining-PO list for the counter and dropdown without waiting
  // for the next navigation to propagate the state.
  const effectiveReviewedIds = new Set([
    ...reviewedPoIds,
    ...((status === 'accepted' || status === 'rejected') ? [String(poId)] : []),
  ]);

  // POs still awaiting review — drives the counter, dropdown, and Next PO button.
  const remainingPoIds = processedPoIds.filter(
    (id) => !effectiveReviewedIds.has(String(id))
  );
  const remainingCount = remainingPoIds.length;
  const currentPoIsRemaining = remainingPoIds.some((id) => String(id) === String(poId));

  const headerFieldRects = useMemo(
    () => buildHeaderFieldCoordinateMap(poData?.header_coordinates, editedData, poData?.grid_coordinates),
    [poData?.header_coordinates, poData?.grid_coordinates, editedData]
  );

  // Derive vendor header field set here (before pdfHighlight) so useMemo can reference it
  // without hitting TDZ.  Uses null-safe checks because poData may still be loading.
  const _vendorKeyEarly = poData ? (VENDOR_DB_TO_SELECT[poData.vendor] || poData.vendor) : '';
  const _mappedFieldsEarly = VENDOR_MAPPED_FIELDS[_vendorKeyEarly] || [];
  const headerKeysToDisplay = editedData
    ? Object.keys(editedData).filter((key) =>
        _mappedFieldsEarly.length > 0 ? _mappedFieldsEarly.includes(key) : true
      )
    : [];

  const pdfHighlight = useMemo(() => {
    if (!poData) return null;
    if (activeTab === 0 && hoverHeaderKey) {
      if (hoverHeaderKey === 'vendor') return null;
      const r = headerFieldRects[hoverHeaderKey];
      if (!r) return null;
      return { page: r.page, x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2 };
    }
    if (activeTab === 1 && hoverLineRow != null && editedLineItems?.[hoverLineRow]) {
      const item = editedLineItems[hoverLineRow];
      if (hoverLineCol) {
        const rects = buildLineItemFieldRects(item.item_coordinates, item);
        const r = rects[hoverLineCol];
        if (r) return { page: r.page, x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2 };
        // Header fallback: safe because buildHeaderFieldCoordinateMap now stores only
        // value-area rects (fallbackToKey:false + unionValueRects), never label coordinates.
        // This lets Chico's customer_style_description (stored in header JSON by the extractor)
        // still get a highlight even though item_coordinates has no rect for it.
        const headerFallback = headerFieldRects[hoverLineCol];
        if (headerFallback) {
          return {
            page: headerFallback.page,
            x1: headerFallback.x1,
            y1: headerFallback.y1,
            x2: headerFallback.x2,
            y2: headerFallback.y2
          };
        }
        return null;
      }
      const uni = lineItemUnionRect(item.item_coordinates);
      if (uni) return { page: uni.page, x1: uni.x1, y1: uni.y1, x2: uni.x2, y2: uni.y2 };
    }
    return null;
  }, [activeTab, poData, hoverHeaderKey, hoverLineRow, hoverLineCol, headerFieldRects, editedLineItems]);

  useEffect(() => {
    setHoverHeaderKey(null);
    setHoverLineRow(null);
    setHoverLineCol(null);
  }, [activeTab]);

  const vendors = [
    { value: 'american-eagle',      label: 'American Eagle'         },
    { value: 'american-eagle-mens', label: 'American Eagle Men'     },
    { value: 'aerie',               label: 'Aerie'                  },
    { value: 'chico',               label: 'Chico'                  },
    { value: 'freepeople',          label: 'Free People'            },
    { value: 'lane-bryant',         label: 'Lane Bryant'            },
    { value: 'mast',                label: 'MAST'                   },
    { value: 'pink',                label: 'Pink'                   },
    { value: 'loft',                label: 'LOFT'                   },
    { value: 'loft-outlet',         label: 'Ann Taylor Loft Outlet' },
    { value: 'ann-taylor',          label: 'Ann Taylor'             },
    { value: 'ann-taylor-factory',  label: 'Ann Taylor Factory'     },
    { value: 'talbots',             label: 'Talbots'                },
  ];

  useEffect(() => {
    setActiveTab(0); // always land on Header when switching to a new PO
    fetchPOData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when poId changes
  }, [poId]);

  // ── Background prefetch: kick off next 2 POs as soon as current one loads ──
  useEffect(() => {
    if (!poData || currentBatchIdx < 0 || processedPoIds.length === 0) return;
    const nextIds = processedPoIds.slice(currentBatchIdx + 1, currentBatchIdx + 3);
    nextIds.forEach(id => prefetchPO(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData]);

  const fetchPOData = async () => {
    setLoadError('');
    setSaveError('');
    setPdfLoadError('');

    delete _poCache[String(poId)];

    setLoading(true);
    setTransitioning(true);
    setPdfFile(null);

    // Reset all inline BR state for the incoming PO
    setBrInlineData(null);
    setBrFieldDecisions({});
    setBrLineItemsDone(false);
    setBrNoBrDataDismissed(false);
    setBrInlineLoading(true);

    // Stamp a fetch ID so responses from previous POs are ignored
    const fetchId = Date.now();
    brFetchIdRef.current = fetchId;

    try {
      const poResponse = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}`);
      const data = poResponse.data;
      const strippedData = stripPoMeta(data);
      const loadedLineItems = Array.isArray(data.line_items) ? data.line_items.map((item) => ({ ...item })) : [];
      const loadedStatus = data.status || 'draft';
      setEditedData(strippedData);
      setEditedLineItems(loadedLineItems);
      setPoData(data);
      setStatus(loadedStatus);
      // Establish baseline so Save changes starts disabled until something actually changes
      setSavedBaseline({
        data:      JSON.stringify(strippedData),
        lineItems: JSON.stringify(loadedLineItems),
        status:    loadedStatus,
      });

      // PDF — fetch bytes via axios so react-pdf does not depend on a second
      // cross-origin fetch through the PDF.js worker.
      try {
        setPdfFile({ data: await fetchPurchaseOrderPdfBytes(poId) });
        setPdfLoadError('');
      } catch (pdfErr) {
        console.error('PDF fetch error:', pdfErr);
        setPdfFile(purchaseOrderPdfUrl(poId));
        setPdfLoadError('');
      }

      // Auto BR verification — only for American Eagle; skip for all other vendors
      const isAEVendor = ['american-eagle', 'american_eagle', 'ae', 'american_eagle_mens', 'american-eagle-mens', 'aerie', 'bae', 'bam', 'bar'].includes((data.vendor || '').toLowerCase());
      if (isAEVendor) {
        axios.get(`${API_BASE_URL}/api/bamboo-rose/verify/${poId}`)
          .then(res  => { if (brFetchIdRef.current === fetchId) setBrInlineData(res.data); })
          .catch(err => { if (brFetchIdRef.current === fetchId) setBrInlineData({ error: err.response?.data?.detail || err.message }); })
          .finally(() => { if (brFetchIdRef.current === fetchId) setBrInlineLoading(false); });
      } else {
        // Non-AE vendor — skip BR check entirely
        if (brFetchIdRef.current === fetchId) setBrInlineLoading(false);
      }

    } catch (err) {
      setLoadError('Failed to load purchase order data');
      setBrInlineLoading(false);
      console.error('Error fetching PO data:', err);
    } finally {
      setLoading(false);
      setTransitioning(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaveLoading(true);
      setSaveError('');
      await axios.put(`${API_BASE_URL}/api/purchase-orders/${poId}`, {
        ...editedData,
        line_items: editedLineItems,
        status
      });
      // Advance baseline so Save changes goes back to disabled
      setSavedBaseline({
        data:      JSON.stringify(editedData),
        lineItems: JSON.stringify(editedLineItems),
        status,
      });
      setPoData((prev) => (prev ? { ...prev, ...editedData, line_items: editedLineItems, status } : prev));
    } catch (err) {
      setSaveError('Failed to save changes');
      console.error('Error saving changes:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAcceptPO = async () => {
    try {
      setSaveLoading(true);
      setSaveError('');
      console.log('[FLOW] Audit Accept: updating existing PO row to status=accepted', { poId });

      // Always write dc_date from BR when accepting a PO.
      // dc_date is never extracted from the PDF so editedData.dc_date is usually null.
      // brInlineData.br_dc_date is the raw BR value returned by the verify endpoint,
      // present whether or not there was a variance.
      const brDcDate = editedData.dc_date
        || brInlineData?.br_dc_date
        || (brInlineData?.header_variances || []).find(v => v.field === 'dc_date')?.br_value
        || null;

      await axios.put(`${API_BASE_URL}/api/purchase-orders/${poId}`, {
        ...editedData,
        ...(brDcDate ? { dc_date: brDcDate } : {}),
        line_items: editedLineItems,
        status: 'accepted'
      });
      setStatus('accepted');
      setPoData((prev) => (
        prev ? { ...prev, ...editedData, line_items: editedLineItems, status: 'accepted' } : prev
      ));
      
      // Auto-advance to the next unreviewed PO (not just the next sequential one)
      if (nextUnreviewedId) {
        setTimeout(() => {
          navigate(`/audit/${nextUnreviewedId}`, { state: batchNavState(true) });
        }, 500);
      }
    } catch (err) {
      setSaveError('Failed to accept PO');
      console.error('Error accepting PO:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRejectPO = async () => {
    try {
      setSaveLoading(true);
      setSaveError('');
      await axios.put(`${API_BASE_URL}/api/purchase-orders/${poId}`, {
        ...editedData,
        line_items: editedLineItems,
        status: 'rejected'
      });
      setStatus('rejected');
      setPoData((prev) => (
        prev ? { ...prev, ...editedData, line_items: editedLineItems, status: 'rejected' } : prev
      ));
      
      // Auto-advance to the next unreviewed PO (not just the next sequential one)
      if (nextUnreviewedId) {
        setTimeout(() => {
          navigate(`/audit/${nextUnreviewedId}`, { state: batchNavState(true) });
        }, 500);
      }
    } catch (err) {
      setSaveError('Failed to reject PO');
      console.error('Error rejecting PO:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const getStatusColor = (s) => {
    switch (s) {
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (s) => {
    switch (s) {
      case 'accepted':
        return <ThumbUp fontSize="small" color="success" />;
      case 'rejected':
        return <ThumbDown fontSize="small" color="error" />;
      case 'draft':
      case 'pending':
      default:
        return <PriorityHigh fontSize="small" color="warning" />;
    }
  };

  const getVendorLabel = (vendor) => {
    const key = VENDOR_DB_TO_SELECT[vendor] || vendor;
    return vendors.find((v) => v.value === key)?.label || vendor;
  };

  const getFieldLabel = (key) => {
    const labels = {
      // ── PO header fields ──
      customer_po_number: 'Customer PO #',
      sub_po: 'Sub PO',
      customer_distribution_channel: 'Distribution Channel',
      initial_order_date: 'Order Date',
      po_version_number: 'PO Version #',
      po_version_date: 'PO Version Date',
      bshipdate: 'B Ship Date',
      dc_date: 'DC Date',
      lane_pickup_date_or_deliver_before: 'Pickup / Deliver Before',
      ship_to: 'Ship To',
      customer: 'Customer / Buyer',
      vendor_name_number: 'Vendor Name / Number',
      factory: 'Factory',
      coo: 'COO',
      shipmode: 'Ship Mode',
      incoterm: 'Incoterm',
      incoterm_origin: 'Incoterm Origin',
      po_status: 'PO Status',
      season: 'Season',
      loft_ticket_type: 'Ticket Type / Packaging',
      ticket_description: 'Ticket Description',
      vendor: 'Customer',
      // ── Core line item fields ──
      customer_style_number: 'Style #',
      customer_style_name: 'Style Name',
      customer_style_description: 'Style Description',
      article_number: 'Article #',
      row_number: 'Row #',
      department: 'Department',
      pack_method_type: 'Pack Method / Type',
      color: 'Color',
      secondary_color: 'Secondary Color',
      size: 'Size',
      upc: 'UPC',
      pieces_to_ship: 'Pieces to Ship',
      unit_of_measure: 'Unit of Measure',
      pack_quantity: 'Pack Quantity',
      qty_of_prepacks: 'Qty of Prepacks',
      us_retail_price: 'US Retail Price',
      sell_price: 'Sell Price',
      total_row_dollar_value: 'Total Row Value ($)',
      prepack_sku: 'Prepack SKU',
      prepack_article_number: 'Prepack Article #',
      class_code: 'Class Code',
      hts_description: 'HTS Description',
      // ── Vendor-specific line item fields ──
      ae_store_type_code: 'AE Store Type Code',
      vss_number: 'VSS #',
      lanebryant_size: 'Lane Bryant Size',
      packaging: 'Packaging',
      lanebryant_vendor_style_number: 'LB Vendor Style #',
      free_people_vendor_style_number: 'FP Vendor Style #',
    };
    return (
      labels[key] ||
      key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    );
  };

  // ── BR gate computations ─────────────────────────────────────────────────────
  // BR checks only apply to American Eagle; all other vendors bypass the gate entirely.
  const _aeKeys = ['american-eagle', 'american_eagle', 'ae', 'american_eagle_mens', 'american-eagle-mens', 'aerie', 'bae', 'bam', 'bar'];
  const isAEVendor = _aeKeys.includes((_vendorKeyEarly || '').toLowerCase())
    || _aeKeys.includes((poData?.vendor || '').toLowerCase());

  // Header variances the user still needs to Accept or Keep.
  // Only include variances for fields that are actually rendered in the header table —
  // if BR returns a variance for a field the vendor mapping hides from the UI, the user
  // can never make a decision on it, which would permanently block the Accept PO gate.
  const brHeaderVariances = (isAEVendor && brInlineData?.br_found && !brInlineData?.exact_match)
    ? (brInlineData.header_variances || []).filter(v =>
        headerKeysToDisplay.length === 0 || headerKeysToDisplay.includes(v.field)
      )
    : [];
  const brHeaderPendingCount = brHeaderVariances.filter(v => !brFieldDecisions[v.field]).length;

  // Fields actually compared to a BR Excel column — only these may show green "Match"
  const brComparedHeaderFields = new Set(brInlineData?.br_compared_header_fields || []);
  const brComparedLineFields = new Set(brInlineData?.br_compared_line_fields || []);

  // Whether there are line-item variances
  const brHasLiVariances = !!(isAEVendor && brInlineData?.br_found && (brInlineData.line_item_variances || []).length > 0);
  const brLiPending      = brHasLiVariances && !brLineItemsDone;

  // "No BR record" = either the whole BR table is empty, or this PO isn't in it
  const brNoBrPending = !!(
    isAEVendor &&
    brInlineData &&
    !brInlineData.error &&
    (!brInlineData.has_br_data || (brInlineData.has_br_data && !brInlineData.br_found)) &&
    !brNoBrDataDismissed
  );

  // Accept PO is blocked while BR is loading or any check is unresolved (AE only)
  const brGateBlocked = isAEVendor && (brInlineLoading || brHeaderPendingCount > 0 || brLiPending || brNoBrPending);

  const brGateTooltip = brGateBlocked
    ? [
        brInlineLoading                ? 'Checking Bamboo Rose data…'                             : '',
        brHeaderPendingCount > 0       ? `${brHeaderPendingCount} header field(s) need BR validation` : '',
        brLiPending                    ? 'Review line item BR changes (click the ! badge)'         : '',
        brNoBrPending                  ? 'Dismiss the BR notification first'                        : '',
      ].filter(Boolean).join(' · ')
    : '';

  // Full-page spinner only on the very first load (no data yet).
  // During PO switches poData is non-null so we fall through to the normal
  // render; the LinearProgress bar + opacity overlay handle the in-progress state.
  if (loading && !poData) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400} gap={2}>
        <CircularProgress size={44} thickness={3} sx={{ color: '#6366f1' }} />
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Loading purchase order…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!poData) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>No purchase order data found</Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const lineKeys =
    editedLineItems?.length > 0
      ? Object.keys(editedLineItems[0]).filter(
          (key) => !['id', 'purchase_order_id', 'item_coordinates'].includes(key)
        )
      : [];

  // headerKeysToDisplay is already declared above pdfHighlight (null-safe version).
  // Re-use _mappedFieldsEarly under a readable alias for lineKeysToDisplay below.
  const mappedFields = _mappedFieldsEarly;

  const lineKeysToDisplay = lineKeys.filter((key) => {
    if (mappedFields.length > 0) return mappedFields.includes(key);
    return true;
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Thin progress bar during PO-to-PO switches — keeps existing content
          visible instead of flashing the full-page spinner */}
      {transitioning && (
        <LinearProgress
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            mb: 1,
            borderRadius: 1,
            height: 3,
            '& .MuiLinearProgress-bar': { backgroundColor: '#6366f1' },
            backgroundColor: 'rgba(99,102,241,0.15)',
          }}
        />
      )}
      <Box
        sx={{
          opacity: transitioning ? 0.55 : 1,
          pointerEvents: transitioning ? 'none' : 'auto',
          transition: 'opacity 0.15s ease',
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.025em',
              mb: 0.5,
              background: 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Document Audit
          </Typography>
          <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>
            PO {poData.po_number || poData.customer_po_number || 'N/A'}
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ width: { xs: '100%', md: 'auto' } }}>
          {isBatchMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: '1 1 100%', sm: 'none' } }}>
              {/* Remaining counter — updates as POs are accepted/rejected */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '8px',
                  backgroundColor: remainingCount === 0
                    ? 'rgba(16,185,129,0.1)'
                    : 'rgba(99,102,241,0.1)',
                  border: `1px solid ${remainingCount === 0
                    ? 'rgba(16,185,129,0.3)'
                    : 'rgba(99,102,241,0.25)'}`,
                }}
              >
                <Typography sx={{
                  fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
                  color: remainingCount === 0 ? '#10b981' : '#818cf8',
                }}>
                  {remainingCount === 0
                    ? `All ${processedPoIds.length} reviewed ✓`
                    : `${remainingCount} of ${processedPoIds.length} remaining`}
                </Typography>
              </Box>

              {/* Jump dropdown — shows ALL batch POs; reviewed ones are marked ✓ */}
              {processedPoIds.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel shrink>Jump to PO</InputLabel>
                  <Select
                    value={String(poId)}
                    label="Jump to PO"
                    onChange={(e) => e.target.value && navigate(`/audit/${e.target.value}`, { state: batchNavState(status === 'accepted' || status === 'rejected') })}
                    sx={{ borderRadius: '8px' }}
                  >
                    {processedPoIds.map((id) => {
                      const label = poNumberMap[String(id)] ? `PO ${poNumberMap[String(id)]}` : `PO #${id}`;
                      const reviewed = effectiveReviewedIds.has(String(id));
                      return (
                        <MenuItem key={id} value={String(id)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <span>{label}</span>
                            {reviewed && (
                              <CheckCircle sx={{ fontSize: 14, color: 'success.main', ml: 'auto' }} />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          <Chip
            icon={getStatusIcon(status)}
            label={status}
            color={getStatusColor(status)}
            variant="filled"
            sx={{ fontWeight: 700, textTransform: 'capitalize', borderRadius: 2 }}
            size="medium"
          />
          {/* "Verify BR" button is in the persistent app header (top bar) */}
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{
              flex: { xs: '1 1 100%', sm: 'none' },
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            Dashboard
          </Button>
        </Box>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 2,
          width: '100%',
          maxHeight: { md: 'calc(100vh - 180px)' },
          minHeight: { md: 'min(720px, calc(100vh - 180px))' }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: '1 1 50%',
            minWidth: 0,
            minHeight: 0,
            height: { xs: '52vh', md: 'auto' },
            maxHeight: { md: 'calc(100vh - 170px)' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'relative',
          }}
        >
          <AuditPdfViewer
            documentKey={String(poId)}
            file={pdfFile}
            externalError={pdfLoadError}
            highlight={pdfHighlight}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: '1 1 50%',
            minWidth: 0,
            minHeight: { xs: 280, md: 0 },
            maxHeight: { md: 'calc(100vh - 170px)' },
            overflow: 'auto',
            p: 2.5,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Extracted data — edit fields, then Accept to finalize (new uploads stay in Draft until you accept).
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={getVendorLabel(poData.vendor)} color="primary" size="small" />
              <Chip label={`ID: ${poData.id}`} size="small" />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ flex: 1, minWidth: 0 }}>
              <Tab label="PO HEADER" />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {`LINE ITEMS (${poData.line_items?.length || 0})`}
                    {brHasLiVariances && !brLineItemsDone && (
                      <Tooltip title="Line item BR changes detected — click to review">
                        <Box
                          onClick={(e) => { e.stopPropagation(); setBrLiDialogOpen(true); }}
                          sx={{
                            width: 18, height: 18, borderRadius: '50%',
                            bgcolor: '#ef4444', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.65rem', fontWeight: 800, flexShrink: 0, cursor: 'pointer',
                            '&:hover': { bgcolor: '#dc2626' },
                          }}
                        >!</Box>
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </Tabs>

            {/* Accept all BR — visible whenever any header OR line-item BR check is pending */}
            {(brHeaderPendingCount > 0 || brLiPending) && !brInlineLoading && (
              <Tooltip
                title={[
                  brHeaderPendingCount > 0 ? `Accept ${brHeaderPendingCount} pending header field(s)` : '',
                  brLiPending ? `Apply BR values to ${(brInlineData?.line_item_variances || []).length} line item row(s)` : '',
                ].filter(Boolean).join(' + ')}
                arrow
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<VerifiedUser sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: '0.72rem', py: 0.4, px: 1.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => {
                    // ── Accept all pending header variances ──
                    if (brHeaderPendingCount > 0) {
                      const newData      = { ...editedData };
                      const newDecisions = { ...brFieldDecisions };
                      brHeaderVariances.forEach(v => {
                        if (!newDecisions[v.field]) {
                          newData[v.field]      = v.br_value || '';
                          newDecisions[v.field] = 'accept';
                        }
                      });
                      setEditedData(newData);
                      setBrFieldDecisions(newDecisions);
                    }
                    // ── Apply all pending line-item variances ──
                    if (brLiPending) {
                      const liVariances = brInlineData?.line_item_variances || [];
                      setEditedLineItems(prev => prev.map(item => {
                        const rowVars = liVariances.find(r => r.line_item_id === item.id);
                        if (!rowVars) return item;
                        const patch = {};
                        rowVars.variances.forEach(v => { patch[v.field] = v.br_value || ''; });
                        return { ...item, ...patch };
                      }));
                      setBrLineItemsDone(true);
                    }
                  }}
                >
                  Accept all BR
                  {(brHeaderPendingCount > 0 || brLiPending) && (
                    <Box component="span" sx={{ ml: 0.5, opacity: 0.85, fontSize: '0.68rem' }}>
                      ({brHeaderPendingCount + (brLiPending ? (brInlineData?.line_item_variances || []).length : 0)})
                    </Box>
                  )}
                </Button>
              </Tooltip>
            )}
          </Box>

          {activeTab === 0 && (
            <Box>
              {/* ── BR loading spinner ───────────────────────────────────── */}
              {brInlineLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                  <CircularProgress size={13} sx={{ color: '#d97706' }} />
                  <Typography sx={{ fontSize: '0.72rem', color: '#d97706' }}>
                    Checking Bamboo Rose data…
                  </Typography>
                </Box>
              )}

              {/* ── BR error (API failure) ───────────────────────────────── */}
              {brInlineData?.error && (
                <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }}
                  onClose={() => setBrInlineData(null)}>
                  BR check failed: {brInlineData.error}
                </Alert>
              )}

              {/* ── No BR data / PO not found in BR ─────────────────────── */}
              {brNoBrPending && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }}
                  icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                  action={
                    <Button size="small" color="inherit" onClick={() => setBrNoBrDataDismissed(true)}
                      sx={{ fontSize: '0.7rem' }}>
                      Dismiss
                    </Button>
                  }
                >
                  {!brInlineData?.has_br_data
                    ? 'No Bamboo Rose data loaded in the system. Sync BR data first, or dismiss to accept anyway.'
                    : `PO ${poData?.customer_po_number} was not found in the latest BR export. Dismiss to accept anyway.`}
                </Alert>
              )}

              {/* ── Perfect match banner ─────────────────────────────────── */}
              {brInlineData?.br_found && brInlineData?.exact_match && !brInlineLoading && (
                <Alert severity="success" sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }}
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}>
                  All fields match Bamboo Rose exactly — no corrections needed.
                </Alert>
              )}

              {/* ── Header table ─────────────────────────────── */}
              <TableContainer sx={{ mt: 0.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700, width: isAEVendor ? '22%' : '30%', fontSize: '0.72rem', color: 'text.secondary', py: 0.5 }}>
                        Field
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: isAEVendor ? '35%' : '70%', fontSize: '0.72rem', color: 'text.secondary', py: 0.5 }}>
                        Extracted Value
                      </TableCell>
                      {isAEVendor && (
                        <TableCell sx={{ fontWeight: 700, width: '43%', fontSize: '0.72rem', py: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <VerifiedUser sx={{ fontSize: 13, color: '#d97706' }} />
                            <Box component="span" sx={{ color: '#d97706' }}>BR Value</Box>
                            {brInlineLoading && <CircularProgress size={10} sx={{ color: '#d97706' }} />}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {headerKeysToDisplay.map((key) => {
                      const value      = editedData[key];
                      const brVariance = brHeaderVariances.find(v => v.field === key);
                      const decision   = brFieldDecisions[key];
                      const isPending  = !!brVariance && !decision;

                      return (
                        <TableRow key={key} hover
                          sx={{ bgcolor: isPending ? 'rgba(245,158,11,0.04)' : undefined }}>

                          {/* ── Field label ── */}
                          <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle', fontSize: '0.8rem', py: 0.5 }}>
                            {getFieldLabel(key)}
                            {isPending && (
                              <Box component="span" sx={{
                                ml: 0.5, display: 'inline-block',
                                width: 7, height: 7, borderRadius: '50%', bgcolor: '#d97706',
                                verticalAlign: 'middle',
                              }} />
                            )}
                          </TableCell>

                          {/* ── Editable value ── */}
                          <TableCell sx={{ py: 0.5 }}>
                            {isReviewableStatus(status) ? (
                              <TextField
                                fullWidth size="small" variant="outlined"
                                value={value || ''}
                                multiline
                                onChange={(e) => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                                onMouseEnter={() => setHoverHeaderKey(key)}
                                onMouseLeave={() => setHoverHeaderKey(null)}
                                onFocus={() => setHoverHeaderKey(key)}
                                onBlur={() => setHoverHeaderKey(null)}
                                sx={isPending ? {
                                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                    { borderColor: 'rgba(245,158,11,0.5)' },
                                } : undefined}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {value || '—'}
                              </Typography>
                            )}
                          </TableCell>

                          {/* ── BR value + Accept/Keep — merged into one column, AE only ── */}
                          {isAEVendor && (
                            <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                              {brVariance ? (
                                decision ? (
                                  /* Resolved state */
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle sx={{ fontSize: 15, color: decision === 'accept' ? '#10b981' : '#64748b', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                      {decision === 'accept' ? 'BR value accepted' : 'Original kept'}
                                    </Typography>
                                    <Button size="small"
                                      onClick={() => setBrFieldDecisions(p => { const n = { ...p }; delete n[key]; return n; })}
                                      sx={{ fontSize: '0.68rem', minWidth: 0, px: 0.75, py: 0.1, color: 'text.disabled', ml: 'auto' }}>
                                      undo
                                    </Button>
                                  </Box>
                                ) : (
                                  /* Pending state — BR value + action buttons side by side */
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* BR value pill */}
                                    <Box sx={{
                                      flex: 1, minWidth: 0,
                                      bgcolor: 'rgba(245,158,11,0.12)',
                                      border: '1px solid rgba(245,158,11,0.35)',
                                      color: '#d97706',
                                      px: 1.25, py: 0.5, borderRadius: '8px',
                                      fontSize: '0.875rem', fontWeight: 600,
                                      wordBreak: 'break-word', lineHeight: 1.4,
                                    }}>
                                      {brVariance.br_value
                                        ? brVariance.br_value
                                        : <em style={{ opacity: 0.5, fontStyle: 'italic', fontSize: '0.8rem' }}>empty</em>}
                                    </Box>
                                    {/* Action buttons stacked vertically */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, flexShrink: 0 }}>
                                      <Button size="small" variant="contained" color="warning"
                                        sx={{ fontSize: '0.72rem', py: 0.35, px: 1.25, minWidth: 64, fontWeight: 700, lineHeight: 1.4 }}
                                        onClick={() => {
                                          setEditedData(p => ({ ...p, [key]: brVariance.br_value || '' }));
                                          setBrFieldDecisions(p => ({ ...p, [key]: 'accept' }));
                                        }}>
                                        Accept
                                      </Button>
                                      <Button size="small" variant="outlined"
                                        sx={{ fontSize: '0.72rem', py: 0.35, px: 1.25, minWidth: 64, lineHeight: 1.4, color: 'text.secondary', borderColor: 'divider' }}
                                        onClick={() => setBrFieldDecisions(p => ({ ...p, [key]: 'dismiss' }))}>
                                        Keep
                                      </Button>
                                    </Box>
                                  </Box>
                                )
                              ) : brInlineData?.br_found && !brInlineLoading && brComparedHeaderFields.has(key) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CheckCircle sx={{ fontSize: 15, color: '#10b981' }} />
                                  <Typography sx={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 500 }}>Match</Typography>
                                </Box>
                              ) : null}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              {/* ── Line item BR changes banner ──────────────────────────── */}
              {brHasLiVariances && !brLineItemsDone && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2, py: 0.75 }}
                  icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
                  action={
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      <Button size="small" variant="outlined" color="warning"
                        sx={{ fontSize: '0.7rem', py: 0.3 }}
                        onClick={() => setBrLiDialogOpen(true)}>
                        View Changes
                      </Button>
                      <Button size="small" color="inherit"
                        sx={{ fontSize: '0.7rem', py: 0.3 }}
                        onClick={() => {
                          // Apply all BR line item values to editedLineItems (staged)
                          const liVariances = brInlineData?.line_item_variances || [];
                          setEditedLineItems(prev => prev.map(item => {
                            const rowVars = liVariances.find(r => r.line_item_id === item.id);
                            if (!rowVars) return item;
                            const patch = {};
                            rowVars.variances.forEach(v => { patch[v.field] = v.br_value || ''; });
                            return { ...item, ...patch };
                          }));
                          setBrLineItemsDone(true);
                        }}>
                        Apply All BR
                      </Button>
                    </Box>
                  }
                >
                  <strong>{(brInlineData?.line_item_variances || []).length} line item row(s)</strong> have BR changes.
                  View the details or apply all BR values at once.
                </Alert>
              )}
              {brHasLiVariances && brLineItemsDone && (
                <Alert severity="success" sx={{ mb: 2, py: 0.5, fontSize: '0.78rem' }}
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}>
                  Line item BR changes handled.
                  <Button size="small" sx={{ ml: 1, fontSize: '0.68rem', py: 0.1 }}
                    onClick={() => setBrLineItemsDone(false)}>
                    Undo
                  </Button>
                </Alert>
              )}

              {editedLineItems && editedLineItems.length > 0 ? (
                <Box>
                  {editedLineItems.map((item, index) => {
                    const matchedLiIds = new Set(brInlineData?.matched_line_item_ids || []);
                    const liMatched = isAEVendor && (
                      matchedLiIds.has(item.id)
                      || !!(brInlineData?.line_item_variances || []).find(r => r.line_item_id === item.id)
                    );
                    const liRowVars = (brInlineData?.line_item_variances || [])
                      .find(r => r.line_item_id === item.id);

                    return (
                    <Accordion key={index} defaultExpanded={index === 0} sx={{ mb: 1, backgroundColor: 'background.paper', borderRadius: 1, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <Typography fontWeight="600" color="primary.main">
                          Line Item #{index + 1}
                        </Typography>
                        {item.customer_style_number && (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                            Style: {item.customer_style_number}
                          </Typography>
                        )}
                        {item.upc && (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                            UPC: {item.upc}
                          </Typography>
                        )}
                        {isAEVendor && brInlineData?.br_found && !brInlineLoading && (
                          <Chip
                            size="small"
                            label={liMatched ? 'BR linked' : 'No BR row'}
                            sx={{
                              ml: 2, height: 22, fontSize: '0.68rem',
                              bgcolor: liMatched ? 'rgba(16,185,129,0.12)' : 'rgba(148,163,184,0.12)',
                              color: liMatched ? '#059669' : '#64748b',
                            }}
                          />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          {lineKeysToDisplay.map((key) => {
                            // Fields that can contain long text get full row width so
                            // the value is always readable without horizontal scrolling.
                            const isWide = [
                              'hts_description',
                              'customer_style_description',
                              'customer_style_name',
                              'ship_to',
                            ].includes(key);
                            const decisionKey = `li:${item.id}:${key}`;
                            const brVariance = liRowVars?.variances?.find(v => v.field === key);
                            const decision = brFieldDecisions[decisionKey];
                            const isPending = !!brVariance && !decision && !brLineItemsDone;

                            return (
                              <Grid item xs={12} sm={isWide ? 12 : 6} md={isWide ? 12 : 4} key={key}>
                                {isReviewableStatus(status) ? (
                                  <Box>
                                    <TextField
                                      fullWidth
                                      label={getFieldLabel(key)}
                                      size="small"
                                      variant="outlined"
                                      value={item[key] || ''}
                                      InputLabelProps={{ shrink: true }}
                                      multiline
                                      maxRows={4}
                                      onMouseEnter={() => {
                                        setHoverLineRow(index);
                                        setHoverLineCol(key);
                                      }}
                                      onMouseLeave={() => {
                                        setHoverLineRow(null);
                                        setHoverLineCol(null);
                                      }}
                                      onFocus={() => {
                                        setHoverLineRow(index);
                                        setHoverLineCol(key);
                                      }}
                                      onBlur={() => {
                                        setHoverLineRow(null);
                                        setHoverLineCol(null);
                                      }}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        setEditedLineItems((prev) =>
                                          prev.map((row, rowIndex) =>
                                            rowIndex === index ? { ...row, [key]: newValue } : row
                                          )
                                        );
                                      }}
                                      sx={isPending ? {
                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                          { borderColor: 'rgba(245,158,11,0.5)' },
                                      } : undefined}
                                    />
                                    {isAEVendor && brInlineData?.br_found && !brInlineLoading && liMatched && (
                                      <Box sx={{ mt: 0.5, minHeight: 22 }}>
                                        {brVariance ? (
                                          decision || brLineItemsDone ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <CheckCircle sx={{ fontSize: 14, color: decision === 'accept' || brLineItemsDone ? '#10b981' : '#64748b' }} />
                                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                {decision === 'accept' || brLineItemsDone ? 'BR applied / reviewed' : 'Original kept'}
                                              </Typography>
                                            </Box>
                                          ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                              <Box sx={{
                                                bgcolor: 'rgba(245,158,11,0.12)',
                                                border: '1px solid rgba(245,158,11,0.35)',
                                                color: '#d97706',
                                                px: 0.75, py: 0.25, borderRadius: '6px',
                                                fontSize: '0.72rem', fontWeight: 600,
                                                maxWidth: '100%', wordBreak: 'break-word',
                                              }}>
                                                BR: {brVariance.br_value
                                                  ? String(brVariance.br_value)
                                                  : <em style={{ opacity: 0.5 }}>empty</em>}
                                              </Box>
                                              <Button size="small" variant="contained" color="warning"
                                                sx={{ fontSize: '0.65rem', py: 0.15, px: 0.75, minWidth: 0 }}
                                                onClick={() => {
                                                  setEditedLineItems(prev =>
                                                    prev.map((row, rowIndex) =>
                                                      rowIndex === index
                                                        ? { ...row, [key]: brVariance.br_value || '' }
                                                        : row
                                                    )
                                                  );
                                                  setBrFieldDecisions(p => ({ ...p, [decisionKey]: 'accept' }));
                                                }}>
                                                Accept
                                              </Button>
                                              <Button size="small" variant="outlined"
                                                sx={{ fontSize: '0.65rem', py: 0.15, px: 0.75, minWidth: 0, color: 'text.secondary', borderColor: 'divider' }}
                                                onClick={() => setBrFieldDecisions(p => ({ ...p, [decisionKey]: 'dismiss' }))}>
                                                Keep
                                              </Button>
                                            </Box>
                                          )
                                        ) : brComparedLineFields.has(key) ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircle sx={{ fontSize: 14, color: '#10b981' }} />
                                            <Typography sx={{ fontSize: '0.7rem', color: '#10b981', fontWeight: 500 }}>Match</Typography>
                                          </Box>
                                        ) : null}
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {getFieldLabel(key)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {item[key] || '—'}
                                    </Typography>
                                  </Box>
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No line items found for this purchase order.
                </Typography>
              )}
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Audit status
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
                disabled={status === 'accepted' || status === 'rejected'}
              >
                <MenuItem value="draft">Draft (not accepted)</MenuItem>
                <MenuItem value="pending">Pending (legacy)</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchPOData}
              disabled={saveLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={() => {
                if (poData) setEditedData(stripPoMeta(poData));
                if (poData) {
                  setEditedLineItems(
                    Array.isArray(poData.line_items) ? poData.line_items.map((item) => ({ ...item })) : []
                  );
                }
              }}
              disabled={saveLoading}
            >
              Reset fields
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={handleSaveChanges}
              disabled={saveLoading || !hasUnsavedChanges}
            >
              {saveLoading ? 'Saving…' : 'Save changes'}
            </Button>
            {isReviewableStatus(status) && (
              <>
                <Tooltip title={brGateTooltip} arrow>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={brInlineLoading ? <CircularProgress size={13} color="inherit" /> : <CheckCircle />}
                      onClick={handleAcceptPO}
                      disabled={saveLoading || brGateBlocked}
                    >
                      {brInlineLoading ? 'Checking BR…' : 'Accept PO'}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<Cancel />}
                  onClick={handleRejectPO}
                  disabled={saveLoading}
                >
                  Reject PO
                </Button>
              </>
            )}

            {/* Batch navigation buttons — visible throughout the batch */}
            {isBatchMode && (
              <>
                {/* Next PO: stays visible until the very last unreviewed PO is done.
                    Uses nextUnreviewedId (skips already-reviewed POs) rather than a
                    positional index so it works regardless of review order. */}
                {nextUnreviewedId && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<NavigateNext />}
                    onClick={() => navigate(`/audit/${nextUnreviewedId}`, { state: batchNavState(status === 'accepted' || status === 'rejected') })}
                    sx={{ borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8', '&:hover': { borderColor: '#818cf8', bgcolor: 'rgba(99,102,241,0.08)' } }}
                  >
                    Next PO
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                >
                  Go to Dashboard
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Batch complete banner — shown only when every PO in the batch is reviewed */}
      {allBatchReviewed && (
        <Box mt={2}>
          <Alert
            severity="success"
            sx={{ border: '1px solid rgba(16,185,129,0.3)', bgcolor: 'rgba(16,185,129,0.05)', alignItems: 'center' }}
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ background: 'linear-gradient(135deg,#6366f1,#818cf8)', whiteSpace: 'nowrap', ml: 1 }}
              >
                Go to Dashboard
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={700}>
              Batch review complete — all {processedPoIds.length} POs reviewed.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All purchase orders in this batch have been accepted or rejected.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Regular reviewed alert — shown when current PO is reviewed but batch is not fully done */}
      {(status === 'accepted' || status === 'rejected') && !allBatchReviewed && (
        <Box mt={2}>
          <Alert severity={status === 'accepted' ? 'success' : 'error'}>
            <Typography variant="body2" fontWeight="bold">
              {status === 'accepted' ? 'PO accepted' : 'PO rejected'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This purchase order has been marked {status}.
            </Typography>
          </Alert>
        </Box>
      )}
      {/* ── Line Item BR Changes Dialog ────────────────────────────────── */}
      {brLiDialogOpen && (() => {
        const liVars = brInlineData?.line_item_variances || [];
        return (
          <Dialog open onClose={() => setBrLiDialogOpen(false)} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: '88vh' } }}>
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningAmberIcon sx={{ color: '#d97706', fontSize: 24 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700}>Line Item BR Changes</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {liVars.length} row(s) differ from Bamboo Rose data ·
                    {brInlineData?.extracted_at && ` BR synced ${new Date(brInlineData.extracted_at).toLocaleString()}`}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Review the changes below. <strong>Apply All BR</strong> will overwrite the differing fields
                  in the line items with BR values (staged — saved on Accept PO / Save Changes).
                  <strong> Dismiss</strong> keeps the current values and clears the gate.
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                {liVars.map((row, ri) => (
                  <Box key={row.line_item_id} sx={{ mb: ri < liVars.length - 1 ? 3 : 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1, color: 'text.primary' }}>
                      Row {row.row_number}
                      {row.identifier ? ` — ${row.identifier}` : ''}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Field</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#ef4444' }}>Current (PDF)</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#d97706' }}>BR Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.variances.map(v => (
                            <TableRow key={v.field} sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.secondary' }}>
                                {getFieldLabel(v.field)}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-block', bgcolor: 'rgba(239,68,68,0.12)', color: '#ef4444',
                                  px: 0.8, py: 0.2, borderRadius: 1, fontSize: '0.76rem', fontFamily: 'monospace' }}>
                                  {v.our_value || <em style={{ opacity: 0.5 }}>empty</em>}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-block', bgcolor: 'rgba(245,158,11,0.12)', color: '#d97706',
                                  px: 0.8, py: 0.2, borderRadius: 1, fontSize: '0.76rem', fontFamily: 'monospace', fontWeight: 600 }}>
                                  {v.br_value || <em style={{ opacity: 0.5 }}>empty</em>}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {ri < liVars.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button variant="outlined" color="inherit"
                onClick={() => { setBrLineItemsDone(true); setBrLiDialogOpen(false); }}>
                Dismiss (keep current)
              </Button>
              <Button variant="contained" color="warning" startIcon={<VerifiedUser />}
                onClick={() => {
                  // Stage all BR values into editedLineItems
                  setEditedLineItems(prev => prev.map(item => {
                    const rowVars = liVars.find(r => r.line_item_id === item.id);
                    if (!rowVars) return item;
                    const patch = {};
                    rowVars.variances.forEach(v => { patch[v.field] = v.br_value || ''; });
                    return { ...item, ...patch };
                  }));
                  setBrLineItemsDone(true);
                  setBrLiDialogOpen(false);
                }}>
                Apply All BR Changes
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
      </Box>{/* end transitioning-opacity wrapper */}
    </Box>
  );
};

export default AuditPage;
